import * as martinez from 'martinez-polygon-clipping';
import { match } from 'ts-pattern';
import { memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { AnyCountersinkHole, BendDirection, BendOrientation, BendType, BendablePlate, CornerBoltHoles, CornerCountersinkHoles, CornerFixingHoles, Finish, Handing, KickPlate, KickPlateBoltHoles, KickPlateCountersinkHoles, KickPlateFixingHoles, RawMaterial, RectangularPlate, RoundPlate, SquarePlate, StrikePlate, StrikePlateShapeType, StrikeTonguePosition } from '../__generated/model/index.js';
import { bendData, calculateBendLengths } from '../data/bend-data.js';
import { defaultCountersinks, defaultCustomMaterials } from '../data/plate-data.js';
import { $BendablePlate, $KickPlateBoltHoles, $KickPlateCountersinkHoles, ApplicationTypes, getHolePositions, round } from '../index.js';
import { generateBoltHoleRecipes, generateCountersinkHoleRecipes, generateCylinderHoleRecipes, generateLocksetHoleRecipes, generateShapeHoleRecipes } from '../recipes/hole.recipes.js';
export const finishLabels = {
    [Finish.brushed]: 'Brushed',
    [Finish.milled]: 'Milled',
    [Finish.chequer]: 'Chequer',
    [Finish.propeller]: 'Propeller'
};
export const rawMaterialLabels = {
    [RawMaterial.ss304]: 'SS304',
    [RawMaterial.ss316]: 'SS316',
    [RawMaterial.aluminium]: 'Aluminium',
    [RawMaterial.brass]: 'Brass'
};
export const minDistanceToEdge = 3;
export const maxMachinePlateSize = {
    length: 2348,
    width: 1219
};
const maxSize = Math.max(maxMachinePlateSize.length, maxMachinePlateSize.width);
export const scaleIndicator = (min, max, plateSize)=>{
    return Math.min(max, Math.max(min, max * (plateSize / maxSize)));
};
const formatDistance = (value)=>`${round(value, 2)}mm`;
const formatTongueOffsetMinimumMessage = (offsetLabel, minimumOffset, mainCornerRadius, jointCornerRadius)=>{
    const jointRadiusSuffix = jointCornerRadius > 0 ? ` and joint corner radius (${formatDistance(jointCornerRadius)})` : '';
    return `Tongue ${offsetLabel} offset value must be at least ${formatDistance(minimumOffset)} to fit selected corner radius (${formatDistance(mainCornerRadius)})${jointRadiusSuffix}`;
};
const getCornerRadiusValidationLimits = (plate)=>{
    const standardMaxCornerRadius = Math.max(0, Math.min(plate.$sizeX, plate.$sizeY) / 2);
    let maxCornerRadius = standardMaxCornerRadius;
    let limitedByBendSide = false;
    let limitedByTongueOffsets = false;
    let limitedByTStrikeMainWidth = false;
    if (plate.hasBend && plate.$customBend) {
        const bendTool = bendData.d90.getTool(plate.thickness);
        if (bendTool) {
            const maxCornerRadiusBySideLength = Math.max(0, Math.min(plate.$customBend.startInnerLength - bendTool.innerRadius, plate.$customBend.endInnerLength - bendTool.innerRadius));
            if (maxCornerRadiusBySideLength < maxCornerRadius) {
                maxCornerRadius = maxCornerRadiusBySideLength;
                limitedByBendSide = true;
            }
        }
    }
    if (plate.type === 'StrikePlate' && plate.shapeType === StrikePlateShapeType.TStrike) {
        const maxCornerRadiusByMainWidth = Math.max(0, (plate.horizontalLength - plate.tongueHorizontalLength) / 2);
        if (maxCornerRadiusByMainWidth < maxCornerRadius) {
            maxCornerRadius = maxCornerRadiusByMainWidth;
            limitedByTStrikeMainWidth = true;
            limitedByTongueOffsets = false;
            limitedByBendSide = false;
        }
        // When one of the tongue offsets is zero, the joint corner is rendered as a straight edge
        // and offset does not constrain main corner radius.
        if (plate.tongueOffsetStart > 0 && plate.tongueOffsetEnd > 0) {
            const maxCornerRadiusByTongueOffsets = Math.max(0, Math.min(plate.tongueOffsetStart, plate.tongueOffsetEnd) - plate.$jointCornerRadius);
            if (maxCornerRadiusByTongueOffsets < maxCornerRadius) {
                maxCornerRadius = maxCornerRadiusByTongueOffsets;
                limitedByTongueOffsets = true;
                limitedByTStrikeMainWidth = false;
                limitedByBendSide = false;
            }
        }
    }
    return {
        maxCornerRadius,
        limitedByBendSide,
        limitedByTongueOffsets,
        limitedByTStrikeMainWidth
    };
};
const getMaximumJointCornerRadius = (plate)=>{
    if (plate.tongueOffsetStart <= 0 || plate.tongueOffsetEnd <= 0) {
        return Infinity;
    }
    const maxJointCornerRadiusByTongueGeometry = plate.$maxJointCornerRadius ?? Infinity;
    const maxJointCornerRadiusByOffsetsAndCornerRadius = Math.max(0, Math.min(plate.tongueOffsetStart, plate.tongueOffsetEnd) - plate.mainCornerRadius);
    return Math.max(0, Math.min(maxJointCornerRadiusByTongueGeometry, maxJointCornerRadiusByOffsetsAndCornerRadius));
};
export const plateExpressions = implementCustomExpressions({
    Plate: {
        $customMaterials: (_s, _state, ctx)=>ctx.publishedData.customMaterials && ctx.publishedData.customMaterials?.length > 0 ? ctx.publishedData.customMaterials : defaultCustomMaterials,
        $allCountersinks: (_s, _state, ctx)=>ctx.publishedData.countersinks && ctx.publishedData.countersinks.length > 0 ? ctx.publishedData.countersinks : defaultCountersinks,
        $availableCountersinks: memoEval((self)=>[
                self.$allCountersinks,
                self.thickness
            ], ($allCountersinks, thickness)=>$allCountersinks.filter((c)=>c.plateThicknessMin <= thickness && c.plateThicknessMax >= thickness)),
        $featureSettings: (s, _state, ctx)=>ctx.publishedData.featureSettings && ctx.publishedData.featureSettings?.length > 0 ? ctx.publishedData.featureSettings.filter((fs)=>fs.plateType === s.type) : [],
        $minimumDistanceHoleToEdge: ()=>minDistanceToEdge,
        $selectedCustomMaterial: (plate)=>{
            const customMaterial = plate.$customMaterials.find((m)=>m.rawMaterial === plate.rawMaterial && m.finish === plate.finish && m.thickness === plate.thickness);
            if (!customMaterial) {
                // This should happen only in dry-run mode, when trying combinations
                // of raw material and thicknes which don't exist.
                return null;
            }
            return customMaterial;
        },
        $activeBend: (self, state)=>{
            const viewStateRecord = state.ephemeralRecords.find((record)=>record.componentInstance.componentType.name === 'viewState');
            if (viewStateRecord?.state?.activeState === ApplicationTypes.ViewState['3d']) {
                return self.$customBend;
            }
            return null;
        },
        $absCoords: ()=>({
                x: 0,
                y: 0
            }),
        // $anchorPoints: self => self.$allAnchorPoints.filter(ap => ap.shapeId === self.id),
        $anchorableShapesChain: ()=>[],
        $boundingBox: (self)=>({
                bottomLeft: {
                    x: 0,
                    y: 0
                },
                topRight: {
                    x: self.$sizeX,
                    y: self.$sizeY
                }
            }),
        $availableRawMaterials: (_s, _state, ctx)=>ctx.publishedData.customMaterials?.map((m)=>m.rawMaterial) ?? [],
        $availableFinishes: (s, _state, ctx)=>(ctx.publishedData.customMaterials?.filter((m)=>m.rawMaterial === s.rawMaterial) ?? []).map((m)=>m.finish),
        $availableThicknesses: (s, _state, ctx)=>ctx.publishedData.customMaterials?.filter((m)=>m.rawMaterial === s.rawMaterial && m.finish === s.finish).map((m)=>m.thickness).sort() ?? [],
        $hasFinishDirection: (plate)=>plate.finish === Finish.brushed,
        $usedCountersinks: (plate)=>[
                ...new Set(plate.$holes.filter((f)=>AnyCountersinkHole.is(f)).map((f)=>`${f.topRadius * 2} x ${f.bottomRadius * 2} mm`))
            ],
        $numHoles: (plate)=>{
            const counts = {};
            for (const hole of plate.$holes){
                const key = hole.$holeType;
                const count = match(hole).when(CornerCountersinkHoles.is, ()=>4).when(CornerBoltHoles.is, ()=>4).when((f)=>$KickPlateCountersinkHoles.is(f) || $KickPlateBoltHoles.is(f), // When both horizontal and vertical count are greater than 0, don't count the corner holes twice.
                (f)=>f.$horizontalCount + f.$verticalCount - (f.$horizontalCount > 0 && f.$verticalCount > 0 ? 4 : 0)).otherwise(()=>1);
                counts[key] = (counts[key] ?? 0) + count;
            }
            return counts;
        },
        $boltHoleRecipes: memoEval((self)=>[
                self.$maxFeatureSize,
                self.$supportsSingleHoles,
                self.$supportsCornerHoles,
                self.$supportsKickPlateHoles,
                self.$hasCornerHoles,
                self.$hasKickPlateHoles
            ], (...args)=>{
            const [$maxFeatureSize, $supportsSingleHoles, $supportsCornerHoles, $supportsKickPlateHoles, $hasCornerHoles, $hasKickPlateHoles] = args;
            return generateBoltHoleRecipes($maxFeatureSize, {
                single: $supportsSingleHoles,
                corner: $supportsCornerHoles && !$hasCornerHoles,
                kickPlate: $supportsKickPlateHoles && !$hasKickPlateHoles
            });
        }),
        $countersinkHoleRecipes: memoEval((self)=>[
                self.$availableCountersinks,
                self.thickness,
                self.$supportsSingleHoles,
                self.$supportsCornerHoles,
                self.$supportsKickPlateHoles,
                self.$hasCornerHoles,
                self.$hasKickPlateHoles
            ], (...args)=>{
            const [$availableCountersinks, thickness, $supportsSingleHoles, $supportsCornerHoles, $supportsKickPlateHoles, $hasCornerHoles, $hasKickPlateHoles] = args;
            return generateCountersinkHoleRecipes($availableCountersinks, thickness, {
                single: $supportsSingleHoles,
                corner: $supportsCornerHoles && !$hasCornerHoles,
                kickPlate: $supportsKickPlateHoles && !$hasKickPlateHoles
            });
        }),
        $locksetHoleRecipes: memoEval((self)=>[
                self.$maxFeatureSize,
                self.$supportsLocksetHoles
            ], ($maxFeatureSize, $supportsLocksetHoles)=>$supportsLocksetHoles ? generateLocksetHoleRecipes($maxFeatureSize) : []),
        $shapeHoleRecipes: memoEval((self)=>[
                self.$maxFeatureSize,
                self.$supportsShapeHoles
            ], ($maxFeatureSize, $supportsShapeHoles)=>$supportsShapeHoles ? generateShapeHoleRecipes($maxFeatureSize) : []),
        $cylinderHoleRecipes: memoEval((self)=>[
                self.$maxFeatureSize,
                self.$supportsCylinderHoles
            ], ($maxFeatureSize, $supportsCylinderHoles)=>$supportsCylinderHoles ? generateCylinderHoleRecipes($maxFeatureSize) : []),
        $maxRawSize: (plate)=>{
            const selectedCustomMaterial = plate.$selectedCustomMaterial;
            if (!selectedCustomMaterial) {
                // This should happen only in dry-run mode, when trying combinations
                // of raw material and thicknes which don't exist.
                // Returning infinity here to avoid triggering invariants.
                return {
                    x: Infinity,
                    y: Infinity
                };
            }
            const maxWidth = Math.min(maxMachinePlateSize.width, selectedCustomMaterial.width);
            const maxLength = Math.min(maxMachinePlateSize.length, selectedCustomMaterial.length);
            if (plate.$sizeX < maxLength && plate.$sizeY < maxWidth) {
                return {
                    x: maxLength,
                    y: maxWidth
                };
            }
            const rotatePlate = plate.finish === Finish.brushed ? plate.finishDirection === 'vertical' : plate.$sizeX < plate.$sizeY;
            return !rotatePlate ? {
                x: maxLength,
                y: maxWidth
            } : {
                x: maxWidth,
                y: maxLength
            };
        },
        $maxRawSizeX: (plate)=>plate.$maxRawSize.x,
        $maxRawSizeY: (plate)=>plate.$maxRawSize.y,
        $maxVerticalLength: (plate)=>{
            const bendEnabled = 'hasBend' in plate && plate.hasBend;
            if (bendEnabled && plate.$bendTool) {
                return Math.min(plate.$bendTool.maximumLength, plate.$maxRawSize.y);
            }
            return plate.$maxRawSize.y;
        },
        $maxHorizontalLength: (plate)=>{
            const bendEnabled = 'hasBend' in plate && plate.hasBend;
            if (bendEnabled && plate.$bendTool) {
                if (plate.$bendOrientation === BendOrientation.Horizontal) {
                    return Math.min(plate.$bendTool.maximumLength, plate.$maxRawSize.x);
                } else {
                    return plate.$maxRawSize.x;
                }
            }
            return plate.$maxRawSize.x;
        },
        $maxFeatureSize: memoEval((self)=>[
                self.$sizeX,
                self.$sizeY
            ], (sizeX, sizeY)=>({
                x: sizeX - minDistanceToEdge * 2,
                y: sizeY - minDistanceToEdge * 2
            })),
        $middleZ: (self)=>self.thickness / 2,
        $sizeX3D: (self)=>{
            const bend = self.$activeBend;
            if (!bend) {
                return self.$sizeX;
            }
            return bend.orientation === BendOrientation.Horizontal ? self.$sizeX : bend.$middlePoint.x;
        },
        $sizeY3D: (self)=>{
            const bend = self.$activeBend;
            if (!bend) {
                return self.$sizeY;
            }
            return bend.orientation === BendOrientation.Horizontal ? bend.$middlePoint.y : self.$sizeY;
        },
        $sizeZ3D: (self)=>{
            const bend = self.$activeBend;
            if (!bend) {
                return 0;
            }
            const bendTool = bendData.d90.getTool(self.thickness);
            if (!bendTool) {
                return 0;
            }
            return bend.direction === BendDirection.Front ? -bend.endInnerLength - self.thickness : bend.endInnerLength + self.thickness;
        },
        $hasCornerHoles: (self)=>!!self.$holes.some((f)=>CornerFixingHoles.is(f)),
        $hasKickPlateHoles: (self)=>!!self.$holes.some((f)=>KickPlateFixingHoles.is(f)),
        // Returns IDs of features which are invisible because they're inside another feature.
        $overlappingFeatureIds: (self)=>{
            const $holes = self.$holes;
            const conflictingIds = new Set();
            for (const $holeA of $holes){
                const polygonsA = $holeA.$polygons;
                for (const $holeB of $holes){
                    if ($holeA.id === $holeB.id) {
                        continue;
                    }
                    const polygonsB = $holeB.$polygons;
                    for (const polygonA of polygonsA){
                        for (const polygonB of polygonsB){
                            const polygonDiff = martinez.diff(polygonA, polygonB);
                            if (polygonDiff.length === 0) {
                                conflictingIds.add($holeA.id);
                            }
                        }
                    }
                }
            }
            return Array.from(conflictingIds);
        },
        // Returns IDs of features which are (possibly) unselectable
        // because their hotspots are covered by another feature's hotspot.
        $overlappingFeatureHotspotIds: (self)=>{
            const maxDistance = self.$conflictingFeatureDistance;
            const conflictingIds = new Set();
            const plateSize = {
                x: self.$sizeX,
                y: self.$sizeY
            };
            const holes = self.$holes.flatMap((f)=>{
                if (CornerBoltHoles.is(f) || CornerCountersinkHoles.is(f)) {
                    return getHolePositions(f.$absCoords, plateSize, {
                        horizontal: 4
                    }).map(({ x, y })=>({
                            id: f.id,
                            x,
                            y
                        }));
                } else if (KickPlateBoltHoles.is(f) || KickPlateCountersinkHoles.is(f)) {
                    return getHolePositions(f.$absCoords, plateSize, {
                        horizontal: f.$horizontalCount,
                        vertical: f.$verticalCount
                    }).map(({ x, y })=>({
                            id: f.id,
                            x,
                            y
                        }));
                } else {
                    return [
                        {
                            id: f.id,
                            x: f.$absCoords.x,
                            y: f.$absCoords.y
                        }
                    ];
                }
            }).sort((a, b)=>a.x === b.x ? a.y - b.y : a.x - b.x);
            let previousHole = holes[0];
            for(let i = 1; i < holes.length; i++){
                const currentHole = holes[i];
                if (Math.abs(currentHole.x - previousHole.x) <= maxDistance && Math.abs(currentHole.y - previousHole.y) <= maxDistance) {
                    conflictingIds.add(currentHole.id);
                    conflictingIds.add(previousHole.id);
                }
                previousHole = currentHole;
            }
            return Array.from(conflictingIds);
        },
        // Returns IDs of features which might have any overlapping issues - with hotspot or feature itself.
        $allOverlappingFeatureIds: (self)=>{
            return Array.from(new Set([
                ...self.$overlappingFeatureIds,
                ...self.$overlappingFeatureHotspotIds
            ]));
        },
        $indicatorLabelFontSize: (self)=>scaleIndicator(4, 64, Math.max(self.$sizeX, self.$sizeY)),
        $sizeXIndicatorFilter: (self)=>!self.$activeBend || self.$activeBend.orientation === BendOrientation.Horizontal,
        $sizeYIndicatorFilter: (self)=>!self.$activeBend || self.$activeBend.orientation === BendOrientation.Vertical,
        $radiusIndicatorExtrusion: (self)=>self.$activeBend?.orientation === BendOrientation.Vertical ? 0 : -self.$sizeIndicatorExtrusion,
        $radiusIndicatorExtrusionZ: (self)=>{
            const bend = self.$activeBend;
            if (!bend || bend.orientation === BendOrientation.Horizontal) {
                return 0;
            }
            return -self.$sizeIndicatorExtrusion * (bend.direction === BendDirection.Front ? -1 : 1);
        },
        $radiusIndicatorX: (self)=>{
            const bend = self.$activeBend;
            return bend?.orientation === BendOrientation.Vertical ? bend.$middlePoint.x : self.$sizeX;
        },
        $radiusIndicatorY: (self)=>{
            const bend = self.$activeBend;
            return bend?.orientation === BendOrientation.Horizontal ? bend.$middlePoint.y : self.$sizeY;
        },
        $radiusIndicatorZ: (self)=>{
            const bend = self.$activeBend;
            if (!bend) {
                return self.$middleZ;
            }
            const distanceFromBend = bend.orientation === BendOrientation.Horizontal ? self.$sizeY - bend.$middlePoint.y : self.$sizeX - bend.$middlePoint.x;
            return distanceFromBend * (bend.direction === BendDirection.Front ? -1 : 1);
        },
        $radiusIndicatorLabel: (self)=>`R=${self.$cornerRadius}`,
        $viewControlsFilter: (self)=>!!self.$customBend,
        $defaultFlatSegmentInfo: (self)=>{
            const plateRadius = self.$cornerRadius;
            return {
                shape: RoundPlate.is(self) ? {
                    type: 'circle',
                    radius: self.diameter / 2
                } : {
                    type: 'roundedRect',
                    cornerRadius: {
                        topLeft: plateRadius,
                        topRight: plateRadius,
                        bottomLeft: plateRadius,
                        bottomRight: plateRadius
                    }
                },
                plate: {
                    sizeX: self.$sizeX,
                    sizeY: self.$sizeY,
                    thickness: self.thickness,
                    rawMaterial: self.rawMaterial
                },
                start: {
                    x: 0,
                    y: 0
                },
                end: {
                    x: self.$sizeX,
                    y: self.$sizeY
                },
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: [],
                bend: {
                    bendType: BendType.d90,
                    innerRadius: 0,
                    orientation: BendOrientation.Horizontal,
                    direction: BendDirection.Front,
                    angle: {
                        from: 0,
                        to: 0
                    }
                }
            };
        },
        $dimensionIndicatorContrastColor: (self)=>{
            if (self.powderCoating) {
                return '#FFFFFF';
            }
            return '#000000';
        }
    },
    BendablePlate: {
        $sizeX: (self)=>{
            const bend = self.$activeBend;
            if (!bend || bend.orientation === BendOrientation.Horizontal) {
                return self.$flatSizeX;
            }
            return bend.startInnerLength + bend.endInnerLength;
        },
        $sizeY: (self)=>{
            const bend = self.$activeBend;
            if (!bend || bend.orientation === BendOrientation.Vertical) {
                return self.$flatSizeY;
            }
            return bend.startInnerLength + bend.endInnerLength;
        },
        $bendLength: (self)=>{
            if (self.$customBend?.orientation === BendOrientation.Horizontal) {
                return self.$sizeX;
            } else if (self.$customBend?.orientation === BendOrientation.Vertical) {
                return self.$sizeY;
            }
            return 0;
        },
        $bendName: (self)=>self.hasBend ? bendData.d90.name : null,
        $bendTool: (self)=>self.hasBend ? bendData.d90.getTool(self.thickness, self.rawMaterial) : null,
        $bendRadius: (self)=>self.hasBend ? bendData.d90.radius : null,
        $bendOrientation: (self)=>self.$customBend?.orientation ?? null,
        $numBends: (s)=>s.hasBend ? 1 : 0,
        $segmentInfo: (s)=>{
            const flatSegmentInfo = s.$defaultFlatSegmentInfo;
            const defaultSegmentInfo = {
                start: null,
                end: null,
                bend: null,
                flat: flatSegmentInfo
            };
            const bend = s.$customBend;
            if (!bend) {
                return defaultSegmentInfo;
            }
            const bendTool = bendData.d90.getTool(s.thickness);
            if (!bendTool) {
                return defaultSegmentInfo;
            }
            const plateRadius = s.$cornerRadius;
            const thickness = s.thickness;
            const innerRadius = bendTool.innerRadius;
            const outerRadius = innerRadius + thickness;
            const isHorizontal = bend.orientation === BendOrientation.Horizontal;
            const isFront = bend.direction === BendDirection.Front;
            const bendSize = bendData.d90.calculateFlatLength(innerRadius, innerRadius, s.thickness, innerRadius, s.rawMaterial);
            const flatPlateSize = bendData.d90.calculateFlatLength(bend.startInnerLength, bend.endInnerLength, s.thickness, bendTool.innerRadius, s.rawMaterial);
            const bendInfo = {
                bendType: BendType.d90,
                innerRadius: bendTool.innerRadius,
                orientation: bend.orientation,
                direction: bend.direction,
                angle: {
                    from: 0,
                    to: 90
                }
            };
            const plateInfo = {
                sizeX: s.$sizeX,
                sizeY: s.$sizeY,
                thickness: s.thickness,
                rawMaterial: s.rawMaterial
            };
            const startFlatLength = bend.startInnerLength - bendTool.innerRadius;
            const endFlatLength = bend.endInnerLength - bendTool.innerRadius;
            const startSegmentInfo = {
                shape: {
                    type: 'roundedRect',
                    cornerRadius: isHorizontal ? {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: plateRadius,
                        bottomRight: plateRadius
                    } : {
                        topLeft: plateRadius,
                        topRight: 0,
                        bottomLeft: plateRadius,
                        bottomRight: 0
                    }
                },
                plate: plateInfo,
                start: {
                    x: 0,
                    y: 0
                },
                end: isHorizontal ? {
                    x: s.$sizeX,
                    y: startFlatLength
                } : {
                    x: startFlatLength,
                    y: s.$sizeY
                },
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: [],
                bend: bendInfo
            };
            const endSegmentInfo = {
                shape: {
                    type: 'roundedRect',
                    cornerRadius: isHorizontal ? {
                        topLeft: plateRadius,
                        topRight: plateRadius,
                        bottomLeft: 0,
                        bottomRight: 0
                    } : {
                        topLeft: 0,
                        topRight: plateRadius,
                        bottomLeft: 0,
                        bottomRight: plateRadius
                    }
                },
                plate: plateInfo,
                start: isHorizontal ? {
                    x: 0,
                    y: flatPlateSize - endFlatLength
                } : {
                    x: flatPlateSize - endFlatLength,
                    y: 0
                },
                end: isHorizontal ? {
                    x: s.$sizeX,
                    y: s.$sizeY
                } : {
                    x: s.$sizeX,
                    y: s.$sizeY
                },
                offset: isHorizontal ? {
                    x: 0,
                    y: startSegmentInfo.end.y + innerRadius + (isFront ? 0 : thickness),
                    z: (isFront ? 1 : -1) * startSegmentInfo.end.y + (isFront ? bendSize - innerRadius : -bendSize + outerRadius)
                } : {
                    x: startSegmentInfo.end.x + innerRadius + (isFront ? 0 : thickness),
                    y: 0,
                    z: isFront ? startSegmentInfo.end.x + bendSize - innerRadius : -startSegmentInfo.end.x - bendSize + outerRadius
                },
                rotation: isHorizontal ? [
                    isFront ? {
                        x: -Math.PI / 2
                    } : {
                        x: Math.PI / 2
                    }
                ] : [
                    {
                        y: isFront ? Math.PI / 2 : -Math.PI / 2
                    }
                ],
                bend: bendInfo
            };
            const halfBendSize = bendSize / 2;
            const bentSegmentInfo = {
                shape: {
                    type: 'bend',
                    cornerRadius: {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                },
                plate: plateInfo,
                start: isHorizontal ? {
                    x: 0,
                    y: startSegmentInfo.end.y
                } : {
                    x: startSegmentInfo.end.x,
                    y: 0
                },
                end: isHorizontal ? {
                    x: s.$sizeX,
                    y: bend.startInnerLength + halfBendSize
                } : {
                    x: startSegmentInfo.end.x + innerRadius,
                    y: s.$sizeY
                },
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: [],
                bend: bendInfo
            };
            return {
                start: startSegmentInfo,
                end: endSegmentInfo,
                bend: bentSegmentInfo,
                // Return the default segment info for 2D view.
                flat: defaultSegmentInfo.flat
            };
        }
    },
    KickPlate: {
        $cornerRadius: (self)=>self.cornerRadius,
        $sizeX: (self)=>self.horizontalLength,
        $sizeY: (self)=>self.verticalLength,
        $flatSizeX: (self)=>self.horizontalLength,
        $flatSizeY: (self)=>self.verticalLength,
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        $center: (self)=>({
                x: self.$sizeX / 2,
                y: self.$sizeY / 2
            }),
        $hiddenAnchorPoints: ()=>[],
        $sizeIndicatorExtrusion: (self)=>-scaleIndicator(10, 100, Math.max(self.$sizeX, self.$sizeY)),
        $bendLength: ()=>0,
        $bendName: ()=>null,
        $bendTool: ()=>null,
        $bendRadius: ()=>null,
        $bendOrientation: ()=>null,
        $numBends: ()=>0,
        $supportsSingleHoles: ()=>false,
        $supportsCornerHoles: ()=>false,
        $supportsKickPlateHoles: ()=>true,
        $supportsLocksetHoles: ()=>false,
        $supportsCylinderHoles: ()=>false,
        $supportsShapeHoles: ()=>false,
        $segmentInfo: (self)=>({
                start: null,
                end: null,
                bend: null,
                flat: self.$defaultFlatSegmentInfo
            })
    },
    RectangularPlate: {
        $cornerRadius: (self)=>self.cornerRadius,
        $flatSizeX: (self)=>self.horizontalLength,
        $flatSizeY: (self)=>self.verticalLength,
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        $center: (self)=>self.$customBend ? self.$customBend.$middlePoint : {
                x: self.$sizeX / 2,
                y: self.$sizeY / 2
            },
        $minHorizontalLength: (self)=>Math.max(self.$minLength, self.cornerRadius * 2),
        $minVerticalLength: (self)=>Math.max(self.$minLength, self.cornerRadius * 2),
        $hiddenAnchorPoints: ()=>[],
        $sizeIndicatorExtrusion: (self)=>-scaleIndicator(10, 100, Math.max(self.$sizeX, self.$sizeY)),
        $supportsSingleHoles: ()=>true,
        $supportsCornerHoles: ()=>true,
        $supportsKickPlateHoles: ()=>false,
        $supportsLocksetHoles: ()=>true,
        $supportsCylinderHoles: ()=>true,
        $supportsShapeHoles: ()=>true
    },
    RoundPlate: {
        $cornerRadius: ()=>0,
        $sizeX: (self)=>self.diameter,
        $sizeY: (self)=>self.diameter,
        $flatSizeX: (self)=>self.diameter,
        $flatSizeY: (self)=>self.diameter,
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        $center: (self)=>self.$customBend ? self.$customBend.$middlePoint : {
                x: self.$sizeX / 2,
                y: self.$sizeY / 2
            },
        $hiddenAnchorPoints: ()=>[],
        $sizeIndicatorExtrusion: (self)=>-scaleIndicator(10, 100, Math.max(self.$sizeX, self.$sizeY)),
        $bendLength: ()=>0,
        $bendName: ()=>null,
        $bendTool: ()=>null,
        $bendRadius: ()=>null,
        $bendOrientation: ()=>null,
        $numBends: ()=>0,
        $supportsSingleHoles: ()=>true,
        $supportsCornerHoles: ()=>false,
        $supportsKickPlateHoles: ()=>false,
        $supportsLocksetHoles: ()=>true,
        $supportsCylinderHoles: ()=>true,
        $supportsShapeHoles: ()=>true,
        $segmentInfo: (self)=>({
                start: null,
                end: null,
                bend: null,
                flat: self.$defaultFlatSegmentInfo
            })
    },
    SquarePlate: {
        $cornerRadius: (self)=>self.cornerRadius,
        $flatSizeX: (self)=>self.sideLength,
        $flatSizeY: (self)=>self.sideLength,
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        $center: (self)=>self.$customBend ? self.$customBend.$middlePoint : {
                x: self.$sizeX / 2,
                y: self.$sizeY / 2
            },
        $minSideLength: (self)=>Math.max(self.$minLength, self.cornerRadius * 2),
        $maxSideLength: (self)=>Math.min(self.$maxRawSize.x, self.$maxRawSize.y),
        $hiddenAnchorPoints: ()=>[],
        $sizeIndicatorExtrusion: (self)=>-scaleIndicator(10, 100, Math.max(self.$sizeX, self.$sizeY)),
        $supportsSingleHoles: ()=>true,
        $supportsCornerHoles: ()=>true,
        $supportsKickPlateHoles: ()=>false,
        $supportsLocksetHoles: ()=>true,
        $supportsCylinderHoles: ()=>true,
        $supportsShapeHoles: ()=>true
    },
    StrikePlate: {
        $isTStrikeFilter: (self)=>self.shapeType === StrikePlateShapeType.TStrike,
        $cornerRadius: (self)=>self.mainCornerRadius,
        $sizeX: (self)=>self.horizontalLength,
        $sizeY: (self)=>self.verticalLength,
        // TODO: Calculate flat size
        $flatSizeX: (self)=>self.horizontalLength,
        $flatSizeY: (self)=>self.verticalLength,
        $center: (self)=>{
            if (self.shapeType === StrikePlateShapeType.DStrike) {
                return {
                    x: self.$sizeX / 2,
                    y: self.$sizeY / 2
                };
            }
            const mainSegmentSizeX = self.horizontalLength - self.$tongueHorizontalLength;
            return self.tonguePosition === StrikeTonguePosition.left ? {
                x: self.$tongueHorizontalLength + mainSegmentSizeX / 2,
                y: self.verticalLength / 2
            } : {
                x: mainSegmentSizeX / 2,
                y: self.verticalLength / 2
            };
        },
        $padding: (self)=>{
            if (self.shapeType === StrikePlateShapeType.DStrike) {
                return self.tonguePosition === StrikeTonguePosition.left ? {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: self.$bendMeasurements.xLength
                } : {
                    top: 0,
                    right: self.$bendMeasurements.xLength,
                    bottom: 0,
                    left: 0
                };
            }
            return self.tonguePosition === StrikeTonguePosition.left ? {
                top: 0,
                right: 0,
                bottom: 0,
                left: self.$tongueHorizontalLength
            } : {
                top: 0,
                right: self.$tongueHorizontalLength,
                bottom: 0,
                left: 0
            };
        },
        $hiddenAnchorPoints: ()=>[],
        $sizeIndicatorExtrusion: (self)=>-scaleIndicator(10, 100, Math.max(self.$sizeX, self.$sizeY)) * 2,
        $bendLength: (self)=>self.$tongueVerticalLength,
        $bendName: (self)=>bendData[self.bendType].name,
        $bendTool: (self)=>bendData[self.bendType].getTool(self.thickness),
        $bendRadius: (self)=>bendData[self.bendType].radius,
        $bendOrientation: ()=>BendOrientation.Vertical,
        $numBends: (s)=>s.hasBend ? 1 : 0,
        $supportsSingleHoles: ()=>true,
        $supportsCornerHoles: ()=>false,
        $supportsKickPlateHoles: ()=>false,
        $supportsLocksetHoles: ()=>false,
        $supportsCylinderHoles: ()=>false,
        $supportsShapeHoles: ()=>true,
        $minHorizontalLength: (self)=>self.shapeType === StrikePlateShapeType.DStrike ? self.$minTongueHorizontalLength : self.$minLength,
        $maxMainCornerRadius: (self)=>getCornerRadiusValidationLimits(self).maxCornerRadius,
        $maxTongueHorizontalLength: (self)=>self.$maxRawSizeX - self.$tStrikeMainWidth,
        $minTongueHorizontalLength: (self)=>{
            // T-Strike without bend can have zero tongue width.
            if (self.shapeType === StrikePlateShapeType.TStrike && !self.hasBend) {
                return 0;
            }
            const bendTool = self.$bendTool;
            if (bendTool && 'minTongueLength' in bendTool && typeof bendTool.minTongueLength === 'number') {
                return bendTool.minTongueLength;
            }
            return bendData[self.bendType]?.minTongueLength ?? 20;
        },
        $maxTongueOffsetStart: (self)=>self.verticalLength - self.$minLength - self.$tongueOffsetEnd,
        $maxTongueOffsetEnd: (self)=>self.verticalLength - self.$minLength - self.$tongueOffsetStart,
        $minTongueOffset: ()=>0,
        $maxTongueCornerRadius: (self)=>bendData[self.bendType].getTool(self.thickness)?.maxCornerRadius ?? self.tongueCornerRadius,
        $tStrikeMainWidth: (self)=>self.horizontalLength - self.tongueHorizontalLength,
        $minTStrikeMainWidth: (self)=>self.shapeType === StrikePlateShapeType.TStrike ? self.$minLength : 0,
        $maxTStrikeMainWidth: (self)=>self.shapeType === StrikePlateShapeType.TStrike ? self.$maxHorizontalLength - self.tongueHorizontalLength : Number.MAX_SAFE_INTEGER,
        $jointCornerRadius: (self)=>self.shapeType === StrikePlateShapeType.TStrike ? self.jointCornerRadius : 0,
        // TODO: Minus bend size
        $maxJointCornerRadius: (self)=>Math.min(self.$tongueOffsetStart, self.$tongueOffsetEnd) > 0 ? Math.min(self.$tongueOffsetStart, self.$tongueOffsetEnd, self.$tongueHorizontalLength) / 2 : Infinity,
        $tongueHorizontalLength: (self)=>{
            if (self.shapeType === StrikePlateShapeType.TStrike) {
                return self.tongueHorizontalLength;
            }
            const bendTool = bendData[self.bendType].getTool(self.thickness);
            if (!bendTool) {
                return 0;
            }
            const bendLengths = calculateBendLengths(bendTool.innerRadius, bendTool.innerRadius + self.thickness, bendTool.angle, self.rawMaterial, bendTool.tailLength);
            return bendLengths.xLength;
        },
        $tongueVerticalLength: (self)=>self.verticalLength - self.$tongueOffsetStart - self.$tongueOffsetEnd,
        $tongueOffsetStart: (self)=>self.shapeType === StrikePlateShapeType.TStrike ? self.tongueOffsetStart : 0,
        $tongueOffsetEnd: (self)=>self.shapeType === StrikePlateShapeType.TStrike ? self.tongueOffsetEnd : 0,
        $tongueIndicatorBottomY: (self)=>self.$tongueOffsetStart,
        $tongueIndicatorTopY: (self)=>self.$sizeY - self.$tongueOffsetEnd,
        $tongueIndicatorExtrusionX: (self)=>self.$sizeIndicatorExtrusion / 2,
        $mainIndicatorStartX: (s)=>s.tonguePosition === StrikeTonguePosition.left ? s.$tongueHorizontalLength : 0,
        $mainIndicatorEndX: (s)=>s.tonguePosition === StrikeTonguePosition.left ? s.$sizeX : s.$sizeX - s.$tongueHorizontalLength,
        $mainIndicatorExtrusion: (s)=>s.$sizeIndicatorExtrusion * (1 / 3),
        $tStrikeMainPlusTongueIndicatorStartX: (s)=>s.tonguePosition === StrikeTonguePosition.left ? s.$tongueHorizontalLength : 0,
        $tStrikeMainPlusTongueIndicatorEndX: (s)=>s.tonguePosition === StrikeTonguePosition.left ? s.$bendMeasurements.xLength : s.$sizeX - s.$bendMeasurements.xLength,
        $tStrikeMainPlusTongueIndicatorExtrusion: (s)=>s.$sizeIndicatorExtrusion * (2 / 3),
        $bendMeasurements: (self)=>{
            const bendTool = self.$bendTool;
            if (!bendTool) {
                return {
                    neutralRadius: 0,
                    insideLength: 0,
                    neutralLength: 0,
                    outsideLength: 0,
                    xLength: 0
                };
            }
            return calculateBendLengths(bendTool.innerRadius, bendTool.innerRadius + self.thickness, bendTool.angle, self.rawMaterial, bendTool.tailLength);
        },
        $segmentInfo: (self)=>{
            const bendTool = bendData[self.bendType].getTool(self.thickness);
            const defaultSegmentInfo = {
                start: null,
                end: null,
                bend: null,
                flat: self.$defaultFlatSegmentInfo
            };
            const hasBend = self.hasBend;
            if (hasBend && !bendTool) {
                return defaultSegmentInfo;
            }
            const isTongueOnRight = self.tonguePosition === StrikeTonguePosition.right;
            const bendAngle = {
                from: 0,
                to: bendTool?.angle ?? 0
            };
            const bendInfo = {
                bendType: self.bendType,
                direction: self.handing === Handing.standard ? BendDirection.Front : BendDirection.Back,
                innerRadius: bendTool?.innerRadius ?? 0,
                orientation: BendOrientation.Vertical,
                angle: bendAngle
            };
            const plateInfo = {
                sizeX: self.$sizeX,
                sizeY: self.$sizeY,
                thickness: self.thickness,
                rawMaterial: self.rawMaterial
            };
            const bendMeasurements = self.$bendMeasurements;
            const bendSizeX = hasBend ? bendMeasurements.xLength : 0;
            const tongueSizeX = self.$tongueHorizontalLength;
            const flatTongueSizeX = tongueSizeX - bendSizeX;
            const mainSegmentOffset = isTongueOnRight ? 0 : tongueSizeX;
            const mainSegmentSizeX = self.horizontalLength - tongueSizeX;
            const mainSegmentSizeY = self.verticalLength;
            const tongueSegmentOffset = isTongueOnRight ? mainSegmentSizeX : bendSizeX;
            const tongueSegmentSizeX = flatTongueSizeX;
            const tongueSegmentSizeY = self.$tongueVerticalLength;
            const mainSegmentInfo = {
                shape: self.shapeType === StrikePlateShapeType.TStrike ? {
                    type: 'tStrike',
                    mainStart: {
                        x: mainSegmentOffset,
                        y: 0
                    },
                    mainEnd: {
                        x: mainSegmentSizeX + mainSegmentOffset,
                        y: mainSegmentSizeY
                    },
                    tongueStart: {
                        x: tongueSegmentOffset,
                        y: self.$tongueOffsetStart
                    },
                    tongueEnd: {
                        x: tongueSegmentSizeX + tongueSegmentOffset,
                        y: mainSegmentSizeY - self.$tongueOffsetEnd
                    },
                    mainCornerRadius: self.mainCornerRadius,
                    tongueCornerRadius: !hasBend ? self.tongueCornerRadius : 0,
                    jointCornerRadius: self.$jointCornerRadius
                } : {
                    type: 'roundedRect',
                    cornerRadius: {
                        topLeft: isTongueOnRight ? self.mainCornerRadius : 0,
                        bottomLeft: isTongueOnRight ? self.mainCornerRadius : 0,
                        topRight: isTongueOnRight ? 0 : self.mainCornerRadius,
                        bottomRight: isTongueOnRight ? 0 : self.mainCornerRadius
                    }
                },
                plate: plateInfo,
                start: {
                    x: Math.min(mainSegmentOffset, tongueSegmentOffset),
                    y: 0
                },
                end: {
                    x: Math.max(mainSegmentSizeX + mainSegmentOffset, tongueSegmentSizeX + tongueSegmentOffset),
                    y: mainSegmentSizeY
                },
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: [],
                bend: bendInfo
            };
            const bentSegmentOffsetStartX = isTongueOnRight ? mainSegmentInfo.end.x : bendSizeX;
            const bentSegmentOffsetEndX = isTongueOnRight ? bentSegmentOffsetStartX + bendSizeX : 0;
            const bentSegmentInfo = {
                shape: {
                    type: 'bend',
                    cornerRadius: {
                        topLeft: isTongueOnRight ? 0 : self.tongueCornerRadius,
                        bottomLeft: isTongueOnRight ? 0 : self.tongueCornerRadius,
                        topRight: isTongueOnRight ? self.tongueCornerRadius : 0,
                        bottomRight: isTongueOnRight ? self.tongueCornerRadius : 0
                    }
                },
                plate: plateInfo,
                start: {
                    x: bentSegmentOffsetStartX,
                    y: self.$tongueOffsetStart
                },
                end: {
                    x: bentSegmentOffsetEndX,
                    y: self.$tongueOffsetStart + tongueSegmentSizeY
                },
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: [],
                bend: bendInfo
            };
            return {
                start: mainSegmentInfo,
                end: null,
                bend: hasBend ? bentSegmentInfo : null,
                flat: null
            };
        }
    }
});
export const plateInvariants = implementCustomInvariants({
    Plate: {
        materialNotAvailable: (plate)=>{
            return plate.$customMaterials.some((m)=>m.rawMaterial === plate.rawMaterial && m.finish === plate.finish && m.thickness === plate.thickness);
        },
        rawMaterialNotAvailable: (plate)=>{
            return plate.$customMaterials.some((m)=>m.rawMaterial === plate.rawMaterial);
        },
        finishNotAvailable: (plate)=>{
            const materials = plate.$customMaterials.filter((m)=>m.rawMaterial === plate.rawMaterial);
            return materials.some((m)=>m.finish === plate.finish);
        },
        thicknessNotAvailable: (plate)=>{
            const hasBend = plate.$customBend;
            const bendType = StrikePlate.is(plate) ? plate.bendType : BendType.d90;
            const materials = plate.$customMaterials.filter((m)=>m.rawMaterial === plate.rawMaterial && m.finish === plate.finish && (!hasBend || bendData[bendType].getTool(m.thickness)));
            return materials.some((m)=>m.thickness === plate.thickness);
        },
        countersinkNotAvailable: (plate)=>{
            const countersinkIds = plate.$availableCountersinks.map((c)=>c.id);
            return !plate.$holes.some((f)=>AnyCountersinkHole.is(f) && !countersinkIds.includes(f.countersinkId));
        },
        powderCoatingAvailable: (plate)=>{
            if (plate.powderCoating) {
                return plate.rawMaterial === RawMaterial.ss304 || plate.rawMaterial === RawMaterial.ss316 || plate.rawMaterial === RawMaterial.aluminium;
            }
            return true;
        },
        powderCoatingAvailableForMaterial: (plate)=>{
            return !plate.powderCoating || !plate.$selectedCustomMaterial || plate.$selectedCustomMaterial.powderCoatingEnabled;
        },
        powderCoatingMountingHoleValidation: (plate)=>{
            if (!plate.powderCoating) {
                return [];
            }
            const bigEnoughHoles = plate.$holes.filter((f)=>f.$innerSize >= plate.$powderCoatingMinHoleSize);
            if (bigEnoughHoles.length > 1 && plate.$powderCoatingMountingHole) {
                return [
                    {
                        type: 'powderCoatingHoleMultipleAvailable',
                        severity: 'warning',
                        message: `Powder coating requires only one ${plate.$powderCoatingMinHoleSize}mm mounting hole.`
                    }
                ];
            }
            if (bigEnoughHoles.length > 0) {
                return [];
            }
            if (KickPlate.is(plate) && plate.$holes.length > 0) {
                // Check if we need bigger hole for powder coating Kick Plate.
                // (there can be only one hole)
                return [
                    {
                        type: 'kickPlateMountingHoleBigEnough',
                        severity: 'warning',
                        message: `Powder coating requires at least one ${plate.$powderCoatingMinHoleSize}mm mounting hole.`
                    }
                ];
            } else {
                return [
                    {
                        type: 'powderCoatingHoleAvailable',
                        severity: 'warning',
                        message: `Powder coating requires at least one ${plate.$powderCoatingMinHoleSize}mm mounting hole.`
                    }
                ];
            }
        },
        cornerRadiusValidation: (plate)=>{
            if (!RectangularPlate.is(plate) && !SquarePlate.is(plate) && !StrikePlate.is(plate)) {
                return [];
            }
            const { maxCornerRadius, limitedByBendSide, limitedByTongueOffsets, limitedByTStrikeMainWidth } = getCornerRadiusValidationLimits(plate);
            if (plate.$cornerRadius <= maxCornerRadius) {
                return [];
            }
            const message = limitedByBendSide ? `Corner radius ${formatDistance(plate.$cornerRadius)} is too large. Maximum is ${formatDistance(maxCornerRadius)} due to bend side length` : limitedByTongueOffsets && StrikePlate.is(plate) ? `Corner radius ${formatDistance(plate.mainCornerRadius)} is too large. Maximum is ${formatDistance(maxCornerRadius)} for current tongue offsets${plate.$jointCornerRadius > 0 ? ` and joint corner radius (${formatDistance(plate.$jointCornerRadius)})` : ''}` : limitedByTStrikeMainWidth && StrikePlate.is(plate) ? `Corner radius ${formatDistance(plate.mainCornerRadius)} is too large. Maximum is ${formatDistance(maxCornerRadius)} for current main width (${formatDistance(plate.horizontalLength - plate.tongueHorizontalLength)})` : `Corner radius ${formatDistance(plate.$cornerRadius)} is too large. Maximum is ${formatDistance(maxCornerRadius)}`;
            return [
                {
                    type: 'cornerRadiusTooLarge',
                    severity: 'fatal',
                    message
                }
            ];
        },
        tongueOffsetStartValidation: (plate)=>{
            if (!StrikePlate.is(plate) || plate.shapeType !== StrikePlateShapeType.TStrike) {
                return [];
            }
            if (plate.tongueOffsetStart === 0) {
                return [];
            }
            const minimumOffset = plate.mainCornerRadius + plate.$jointCornerRadius;
            if (plate.tongueOffsetStart >= minimumOffset) {
                return [];
            }
            return [
                {
                    type: 'tongueOffsetStartTooSmallForCornerRadii',
                    severity: 'fatal',
                    message: formatTongueOffsetMinimumMessage('bottom', minimumOffset, plate.mainCornerRadius, plate.$jointCornerRadius)
                }
            ];
        },
        tongueOffsetEndValidation: (plate)=>{
            if (!StrikePlate.is(plate) || plate.shapeType !== StrikePlateShapeType.TStrike) {
                return [];
            }
            if (plate.tongueOffsetEnd === 0) {
                return [];
            }
            const minimumOffset = plate.mainCornerRadius + plate.$jointCornerRadius;
            if (plate.tongueOffsetEnd >= minimumOffset) {
                return [];
            }
            return [
                {
                    type: 'tongueOffsetEndTooSmallForCornerRadii',
                    severity: 'fatal',
                    message: formatTongueOffsetMinimumMessage('top', minimumOffset, plate.mainCornerRadius, plate.$jointCornerRadius)
                }
            ];
        },
        jointCornerRadiusValidation: (plate)=>{
            if (!StrikePlate.is(plate) || plate.shapeType !== StrikePlateShapeType.TStrike) {
                return [];
            }
            const maximumJointCornerRadius = getMaximumJointCornerRadius(plate);
            if (plate.$jointCornerRadius <= maximumJointCornerRadius) {
                return [];
            }
            return [
                {
                    type: 'jointCornerRadiusTooLargeForCornerRadii',
                    severity: 'fatal',
                    message: `Joint corner radius value must be at most ${formatDistance(maximumJointCornerRadius)} to fit selected corner radius (${formatDistance(plate.mainCornerRadius)}), current tongue offsets (${formatDistance(plate.tongueOffsetStart)} bottom, ${formatDistance(plate.tongueOffsetEnd)} top), and tongue width (${formatDistance(plate.tongueHorizontalLength)}).`
                }
            ];
        },
        hasBendTool: (plate)=>{
            if (BendablePlate.is(plate) && plate.hasBend) {
                return !!bendData.d90.getTool(plate.thickness);
            } else if (StrikePlate.is(plate) && plate.hasBend) {
                return !!bendData[plate.bendType].getTool(plate.thickness);
            }
            return true;
        },
        bendNotTooLong: (plate)=>{
            const bendLength = plate.$bendLength;
            if (bendLength > 0) {
                const bendTool = plate.$bendTool;
                if (!bendTool) {
                    return true;
                }
                return bendTool.maximumLength >= bendLength;
            }
            return true;
        },
        strikePlateWidthSmallerThanTongueWidth: (plate)=>{
            if (!StrikePlate.is(plate) || plate.shapeType !== StrikePlateShapeType.TStrike) {
                return true;
            }
            return plate.$tStrikeMainWidth >= plate.$minLength;
        },
        notEnoughSpaceForBend: (plate)=>{
            if (!$BendablePlate.is(plate) || !plate.$customBend) {
                return [];
            }
            const customBend = plate.$customBend;
            const violations = [];
            if (customBend.startInnerLength < customBend.$startLengthMin) {
                violations.push({
                    type: 'startLengthTooShort',
                    severity: 'fatal',
                    message: `Start length is ${round(customBend.startInnerLength, 2)} mm, which is smaller than minimum of ${round(customBend.$startLengthMin, 2)} mm`
                });
            }
            if (customBend.endInnerLength < customBend.$endLengthMin) {
                violations.push({
                    type: 'endLengthTooShort',
                    severity: 'fatal',
                    message: `End length is ${round(customBend.endInnerLength, 2)} mm, which is smaller than minimum of ${round(customBend.$endLengthMin, 2)} mm`
                });
            }
            if (violations.length > 0) {
                violations.unshift({
                    type: 'notEnoughSpaceForBend',
                    severity: 'fatal',
                    message: 'The plate is too small to fit the bend'
                });
            }
            return violations;
        }
    }
});

//# sourceMappingURL=plate.js.map